import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";

import {
  getAudioStatus,
  getVoices,
  getVoices3p,
  generateSpeech,
  generateAudio3p,
  audioMux,
} from "@app/services/mogrt";

import { useMogrtStatus } from "@app/hooks/mogrtTemplates";
import { useZoltarMutation } from "@app/hooks/zoltar";
import { readZoltarGenerateJsonWithFallback } from "@app/services/zoltar";
import { useFireflyStatus } from "@app/hooks/firefly";
import { pollStatus } from "@app/services/adobeFirefly";
import { useUploadFile } from "@app/hooks/s3";


export const TTS_MODEL = {
  FIREFLY: "firefly",
  ELEVENLABS: "elevenlabs",
};

export const DEFAULT_TTS_MODEL = TTS_MODEL.FIREFLY;

export const TTS_MODEL_OPTIONS = [
  { id: TTS_MODEL.FIREFLY, label: "Firefly Speech" },
  { id: TTS_MODEL.ELEVENLABS, label: "ElevenLabs" },
];

export const textToSpeechKey = {
  all: ["textToSpeech"],
  voices: () => [...textToSpeechKey.all, "voices"],
  voices3p: () => [...textToSpeechKey.all, "voices-3p"],
};

export const useVoices = (options = {}) => {
  return useQuery({
    queryKey: textToSpeechKey.voices(),
    queryFn: async ({ signal }) => {
      const voicesResponse = await getVoices({ signal });
      return (voicesResponse?.voices ?? [])
        .filter((voice) => voice?.status === "Active")
        .reduce((acc, voice) => {
          const prefix = voice.voiceId.replace(/_\d+$/, "");
          if (!acc.find((v) => v.voiceId.replace(/_\d+$/, "") === prefix)) {
            acc.push(voice);
          }
          return acc;
        }, []);
    },
    meta: {
      errorMessage: "Failed to fetch voices. Please try again.",
    },
    ...options,
  });
};

const VOICE_GENDER_MAP = {
  female: "f",
  male: "m",
  "non-binary": "nb",
  "non_binary": "nb",
  nonbinary: "nb",
  neutral: "nb",
};

const prettifyLabel = (value) =>
  typeof value === "string"
    ? value.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())
    : "";

const normalizeVoice3p = (item = {}) => {
  const voice = item.voice ?? item;
  const labels = voice.labels ?? {};
  const gender = (voice.gender ?? labels.gender ?? "").toLowerCase();

  return {
    voiceId: item.voiceId ?? item.voice_id ?? voice.voiceId ?? voice.id,
    displayName: voice.name ?? voice.displayName ?? item.voiceId,
    ageGroup: prettifyLabel(voice.ageGroup ?? labels.age ?? ""),
    gender: VOICE_GENDER_MAP[gender] ?? gender,
    sampleURL: voice.previewUrl ?? voice.preview_url ?? voice.sampleURL ?? "",
    status: voice.status ?? item.status ?? "Active",
  };
};

export const useVoices3p = (options = {}) => {
  return useQuery({
    queryKey: textToSpeechKey.voices3p(),
    queryFn: async ({ signal }) => {
      const voicesResponse = await getVoices3p({ signal });
      return (voicesResponse?.voices ?? [])
        .map(normalizeVoice3p)
        .filter((voice) => voice.voiceId);
    },
    meta: {
      errorMessage: "Failed to fetch ElevenLabs voices. Please try again.",
    },
    ...options,
  });
};

export const useGenerateSpeech = (options = {}) => {
  const [progress, setProgress] = useState(null);

  const renderStatus = useMogrtStatus(
    {
      retryDelay: 2000,
      onSettled: () => {
        setProgress(null);
      },
    },
    {
      onStatus: (statusResponse) => {
        if (statusResponse?.percentCompleted) {
          const percentCompleted = parseInt(
            statusResponse.percentCompleted,
            10,
          );
          setProgress((prev) => {
            if (prev === null) {
              return percentCompleted;
            } else {
              return Math.min(Math.max(prev, percentCompleted), 99);
            }
          });
        }
      },
      getStatusFn: getAudioStatus,
    },
  );

  const mutation = useMutation({
    mutationFn: async (variables, { signal }) => {
      const resolvedVoiceId =
        variables?.voiceId ??
        variables?.voiceConfig?.voiceId ??
        "v123";

      const voiceId = resolvedVoiceId?.split("_")?.[0];

      const voiceConfig = {
        pitch: variables?.voiceConfig?.pitch ?? 0,
        speed: variables?.voiceConfig?.speed ?? 0.95,
      };

      const payload = {
        voiceId,
        script: {
          mediaType: "text/plain",
          localeCode: "en-US", // TODO: make this dynamic
        },
        output: {
          mediaType: "audio/wav",
          voiceConfig,
          metadata: { name: "firefly:internal:text_to_speech_v2" }
        },
      };

      if (variables?.text) {
        payload.script.text = parseScriptText(variables.text, {
          voiceId,
          localeCode: payload.script.localeCode,
          ...voiceConfig,
        });
      } else if (variables?.url) {
        payload.script.source = {
          url: variables.url,
        };
      } else {
        throw new Error("Either text or url must be provided");
      }

      const generateResponse = await generateSpeech(payload, { signal });

      const statusResponse = await renderStatus.mutateAsync({
        jobId: generateResponse?.jobId ?? "",
        signal,
      });

      const outputUrl = statusResponse?.output?.destination?.url;

      if (!outputUrl) {
        throw new Error("Render succeeded but no output URL returned", {
          cause: statusResponse,
        });
      }

      return outputUrl;
    },
    onError: (error, variables, onMutateResult, context) => {
      if (error?.cause) {
        context.meta.errorMessage = `${error.message}. ${error.cause?.error?.data?.message ?? ""}`;
      }
    },
    meta: {
      errorMessage: "Failed to generate speech. Please try again.",
    },
    ...options,
  });

  return {
    ...mutation,
    progress,
  };
};

const ELEVENLABS_MIN_SPEED = 0.7;
const ELEVENLABS_MAX_SPEED = 1.2;

const extractAudio3pUrl = (data = {}) => {
  const outputs = data?.outputs ?? data?.result?.outputs ?? [];
  const audio = outputs?.[0]?.audio;

  return (
    audio?.presignedUrl ??
    audio?.url ??
    outputs?.[0]?.destination?.url ??
    data?.output?.destination?.url ??
    data?.result?.source?.url ??
    null
  );
};

const getAudio3pStatus = async (params, options = {}) => {
  const response = await pollStatus(params, options);

  if (!response.ok && response.status !== 429) {
    return { status: "failed", ...response.data };
  }

  const data = response.data ?? {};

  if (data?.status === "failed") {
    return { status: "failed", ...data };
  }

  const url = extractAudio3pUrl(data);

  return {
    status: url ? "succeeded" : "in_progress",
    ...data,
  };
};

export const useGenerateSpeech3p = (options = {}) => {
  const [progress, setProgress] = useState(null);

  const renderStatus = useFireflyStatus(
    {
      onSettled: () => {
        setProgress(null);
      },
    },
    {
      onStatus: () => {
        setProgress((prev) => (prev === null ? 0 : prev));
      },
      getStatusFn: getAudio3pStatus,
    },
  );

  const mutation = useMutation({
    mutationFn: async (variables, { signal }) => {
      const voiceId = variables?.voiceId ?? variables?.voiceConfig?.voiceId;

      if (!voiceId) {
        throw new Error("A voice must be selected to generate speech");
      }

      const rawSpeed = variables?.voiceConfig?.speed ?? 1;
      const speed = Math.min(
        Math.max(rawSpeed, ELEVENLABS_MIN_SPEED),
        ELEVENLABS_MAX_SPEED,
      );

      let prompt;
      if (variables?.text) {
        prompt = getPronunciations(variables.text).reduce(
          (acc, { match, key }) => acc.replaceAll(match, key),
          variables.text,
        );
      } else {
        throw new Error("text must be provided");
      }

      const payload = {
        prompt,
        voiceId,
        modelId: "elevenlabs",
        modelVersion: "eleven_multilingual_v2",
        modelSpecificPayload: {
          voice_settings: {
            use_speaker_boost: true,
            similarity_boost: 0.75,
            style: 0,
            speed,
            stability: 0.5,
          },
        },
      };

      const generateResponse = await generateAudio3p(payload, { signal });

      const statusUrl =
        generateResponse?.links?.result?.href ??
        generateResponse?.statusUrl ??
        "";

      if (!statusUrl) {
        throw new Error("Generate succeeded but no status URL returned", {
          cause: generateResponse,
        });
      }

      const statusResponse = await renderStatus.mutateAsync({
        statusUrl,
        signal,
      });

      const outputUrl = extractAudio3pUrl(statusResponse);

      if (!outputUrl) {
        throw new Error("Render succeeded but no output URL returned", {
          cause: statusResponse,
        });
      }

      return outputUrl;
    },
    onError: (error, variables, onMutateResult, context) => {
      if (error?.cause) {
        context.meta.errorMessage = `${error.message}. ${error.cause?.error?.data?.message ?? ""}`;
      }
    },
    meta: {
      errorMessage: "Failed to generate speech. Please try again.",
    },
    ...options,
  });

  return {
    ...mutation,
    progress,
  };
};

export const useFixPronunciation = (options = {}) => {
  const fixPronunciation = useZoltarMutation(
    {
      onSuccess: (data, variables) => {},
    },
    (data) =>
      readZoltarGenerateJsonWithFallback(
        data,
        '{"items":[]}',
        "Failed to fix pronunciation",
      ),
  );

  return useMutation({
    mutationFn: async (variables) => {
      const pronunciations = getPronunciations(variables.text);
      const text = pronunciations.reduce(
        (acc, { match, key }) => acc.replaceAll(match, key),
        variables.text,
      );
      const parts = Object.fromEntries(
        Object.entries(variables.parts ?? {}).map(([k, v]) => [
          k,
          pronunciations.reduce((acc, { match, key }) => acc.replaceAll(match, key), v),
        ]),
      );

      const data = await fixPronunciation.mutateAsync({
        promptTemplate: [
          { role: "user", prompt_template: text },
        ],
        systemPromptId: "common-pronunciation",
        text: text,
      });

      const utterance = (data?.items || []).toSorted(
        (a, b) => b.value?.length - a.value?.length,
      );
      const partKeys = Object.keys(parts);
      const allTexts = [text, ...partKeys.map((k) => parts[k])];

      for (const { key, value } of utterance) {
        const existingTagRegex = new RegExp(`\\[[\\w\\s]*${key}[\\w\\s]*\\|`);
        for (let i = 0; i < allTexts.length; i++) {
          allTexts[i] = allTexts[i].replaceAll(key, (match) =>
            existingTagRegex.test(allTexts[i]) ? match : `[${key}|${value}]`,
          );
        }
      }

      const newText = allTexts[0];
      partKeys.forEach((k, i) => {
        parts[k] = allTexts[i + 1];
      });

      return { text: newText, parts, utterance };
    },
    meta: {
      errorMessage: "Failed to fix pronunciation. Please try again.",
    },
    ...options,
  });
};

export const useAudioMux = (options = {}) => {
  const [progress, setProgress] = useState(null);

  const renderStatus = useFireflyStatus({
    onStatus: (statusResponse) => {
      if (statusResponse?.status === "succeeded") {
        setProgress((prev) => {
          if (prev === null) {
            return 100;
          } else {
            return prev;
          }
        });
      }
    },
  });

  const mutation = useMutation({
    mutationFn: async (audios, { signal }) => {
      if (!Array.isArray(audios) || audios.length === 0) {
        throw new Error("audios must be a non-empty array");
      }

      const generateResponse = await audioMux(audios, { signal });

      const statusResponse = await renderStatus.mutateAsync({
        statusUrl: generateResponse?.statusUrl ?? "",
        signal,
      });

      const outputUrl = statusResponse?.result?.source?.url;

      if (!outputUrl) {
        throw new Error("Render succeeded but no output URL returned", {
          cause: statusResponse,
        });
      }

      return outputUrl;
    },
    onError: (error, variables, onMutateResult, context) => {
      if (error?.cause) {
        context.meta.errorMessage = `${error.message}. ${error.cause?.error?.data?.message ?? ""}`;
      }
    },
    meta: {
      errorMessage: "Failed to mux audio. Please try again.",
    },
    ...options,
  });

  return mutation;
};

export const useGenerateAudio = (options = {}) => {
  const generateSpeech = useGenerateSpeech();
  const audioMuxMutation = useAudioMux();
  const uploadFile = useUploadFile();

  return useMutation({
    mutationFn: async (
      { text, voiceId, voiceConfig, email, selectedFolder },
      { signal },
    ) => {
      const generateResult = await generateSpeech.mutateAsync(
        { text, voiceId, voiceConfig },
        { signal },
      );

      const muxResult = await audioMuxMutation.mutateAsync(
        [
          {
            source: { url: generateResult },
            volume: 100,
            inset: 34.0,
            start: 0.0,
            duration: 25.0,
          },
        ],
        { signal },
      );

      const emailPrefix = email?.split("@")?.[0];
      const filePath = `${emailPrefix}/mogrt/${selectedFolder}/${selectedFolder}.wav`;

      const uploadResult = await uploadFile.mutateAsync(
        { filePath, fileURL: muxResult },
        { signal },
      );

      return uploadResult;
    },
    meta: {
      errorMessage: "Failed to generate and upload audio. Please try again.",
    },
    ...options,
  });
};

export function getPronunciations(text) {
  const regex = /\[([^\|\[\]]+)\|([^\|\[\]]+)\]/g;
  const pronunciations = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    const [_, key, value] = match;
    pronunciations.push({ match: _, key, value });
  }
  return pronunciations;
}

function parseScriptText(text, voiceConfig = {}) {
  const fragment = document.createElement("template");
  const speakNode = document.createElement("speak");
  const voiceNode = document.createElement("voice");
  voiceNode.setAttribute("name", voiceConfig.voiceId);
  voiceNode.setAttribute("language", voiceConfig.localeCode);

  const pitchNode = document.createElement("prosody");
  pitchNode.setAttribute("pitch_shift", `${voiceConfig.pitch}`);
  pitchNode.setAttribute("speaking_rate", `${voiceConfig.speed}`);

  const pronunciations = getPronunciations(text);
  let htmlText = text;
  pronunciations.forEach(({ match, key, value }) => {
    const phonemeNode = document.createElement("phoneme");
    phonemeNode.setAttribute("alphabet", "text");
    phonemeNode.setAttribute("ph", value);
    phonemeNode.textContent = key;
    htmlText = htmlText.replace(match, phonemeNode.outerHTML);
  });

  pitchNode.innerHTML = htmlText;

  fragment.appendChild(speakNode);
  speakNode.appendChild(voiceNode);
  voiceNode.appendChild(pitchNode);

  return fragment.innerHTML
    .replace(/"/g, "'")
    .replace(/\n/g, "<s />")
    .replace(/[\s]+/g, " ");
}
